import type { ComponentType, ReactNode } from 'react'

import { cn } from '../../lib/utils'
import {
  CallArt,
  OrderArt,
  PassArt,
  RewardArt,
  SeatArt,
  TicketArt,
} from './announcement-art'

/**
 * FeatureAnnouncement — the "what's new" band, as a shared block.
 *
 * Three modules are built and released but not yet switched on for most
 * venues, and nothing was telling them. This is the strip that does, rendered
 * on every surface a customer already passes through: the accounts sign-in and
 * the marketing homepage. It lives here rather than in either app because a
 * second copy would drift, and the drawn figures are the part that must not.
 *
 * ── WHAT THE HOST OWNS ──────────────────────────────────────────────────────
 *
 * Only the ACTIONS, passed as a node. The two hosts genuinely differ: accounts
 * is a separate origin and links out to absolute stampede.ai URLs in a new tab,
 * while wir opens its own sales dialog and routes internally. Everything else —
 * the copy, the figures, the grid, the rhythm — is fixed here so the two
 * surfaces stay identical.
 *
 * The host also owns visibility. `.stone-band` (package stylesheet) gives it an
 * always-stone surface in both themes; wrap it in whatever section chrome the
 * page uses.
 *
 * ── WIDTH ───────────────────────────────────────────────────────────────────
 *
 * The band keys its layout off its OWN width (`@container`), never the
 * viewport. It has to: on the accounts sign-in it sits inside the right-hand
 * brand pane, half a desktop wide, where a viewport breakpoint would swear the
 * screen is huge and lay the three out 3-up in a column that cannot hold them.
 *
 * Two switches, at the widths each layout actually needs:
 *
 *  • `@5xl` (about 960px of band): the three sit in a 3-up grid. A cell needs
 *    roughly 270px to hold its figure plus a ONE-line description, and with the
 *    cells' own dividers and the host's gutter that is about 960px of band. Any
 *    earlier and the descriptions wrap to two lines, which drops that feature's
 *    name off the baseline the other two sit on (seen at 1920px, where the
 *    accounts pane is 915px). Below it the three collapse to a single
 *    hairline-divided column, keeping the figure beside its label rather than
 *    shrinking the row.
 *  • `@4xl` (about 840px): the actions sit beside the heading. The heading at
 *    its 28px cap, its pill and two `lg` buttons measure about 660px on one
 *    row, so this fits with room from here; below it the actions stack under
 *    the heading.
 *
 * Hosts should size their buttons for touch (>= 44px) since this DOES render on
 * a phone — the accounts sign-in opts out of small screens entirely, but that is
 * its own choice, not the block's.
 */

/**
 * Every feature that has ever been announced, current or superseded.
 *
 * One union rather than one per array, because a host's destination map is
 * keyed by it and has to stay exhaustive across BOTH lists — the previous three
 * keep their rows on the "what's new" page long after they stop being the
 * headline, and a narrowed type would quietly drop their links.
 */
export type AnnouncedFeatureId =
  | 'smart-seating'
  | 'table-ordering'
  | 'square-rewards'
  | 'wallets'
  | 'phone-bookings'
  | 'ticketed-events'

export interface AnnouncedFeature {
  /**
   * Stable slug. Hosts key their own destination off this rather than off
   * `name`, so the copy can be reworded without silently breaking a link.
   */
  id: AnnouncedFeatureId
  name: string
  /** One clause, about 40 characters. See the note on ANNOUNCED_FEATURES. */
  line: string
  /**
   * Two or three sentences: what it does, at the top level, for someone who
   * will never click through.
   *
   * REQUIRED, and deliberately so. The strip surfaces (the band, the pop-up)
   * render `line` only and must stay three tight rows, so it is tempting to
   * treat this as optional and let a feature ship with a name and a link. That
   * is exactly what the "what's new" page used to be, and a name plus a View
   * button asks the reader to go and find out what they are even looking at.
   * Making it required means a feature cannot be announced without saying what
   * it is.
   *
   * Say what it DOES and what changes for the venue. The full write-up lives on
   * the marketing site's feature log; this is the version that has to stand
   * alone inside the product.
   */
  summary: string
  art: ComponentType
}

/**
 * What gets announced, in one place for every surface.
 *
 * Each of these has a full write-up inside the product and on the marketing
 * site's feature log; this is the short form, sized for a strip. Keep `line` to
 * about 40 characters: it sits beside the figure in a third of the band's
 * width, and a line that wraps makes its cell taller, which drops that
 * feature's name off the shared baseline the other two sit on.
 *
 * ── WHY THIS IS A ROTATION, NOT A LIST ──────────────────────────────────────
 *
 * Three, always. A banner listing six things is not an announcement, it is a
 * menu, and it stops being read. Three is also what the grid is built for: a
 * fourth needs the layout revisited, not just the array.
 *
 * So a new release does not APPEND here — it takes the top three and pushes the
 * outgoing three into `PREVIOUS_FEATURES`, which no band renders and only the
 * product's permanent "what's new" page reads. The strip stays the size it was
 * designed at, and nothing that was announced silently disappears.
 *
 * Bump `ANNOUNCEMENT_ID` in Connect's `use-whats-new-seen` in the same change,
 * or everyone who dismissed the last set never meets this one.
 */
export const ANNOUNCED_FEATURES: AnnouncedFeature[] = [
  {
    art: SeatArt,
    id: 'smart-seating',
    summary:
      'A booking system picks a table once, when the booking is made, and never looks at it again, so by Friday couples are sitting on four-seaters and the gaps left behind fit nobody. An algorithm weighs every request against the whole room and quietly reshuffles bookings that have not arrived yet, so times you were turning away simply become bookable. Test it on your own book first: the replay is read-only and shows what each night would have been worth.',
    line: 'Shuffles tables to maximise capacity',
    name: 'Smart Seating',
  },
  {
    art: OrderArt,
    id: 'table-ordering',
    summary:
      "A guest scans the code on the table, reads your live Square menu, orders and pays by card, with no app to download and no waiting to catch someone's eye. The order reaches the till already tagged with the table it came from, and the menu is your Square catalogue rather than a second one to keep in step.",
    line: 'Guests order and pay at the table',
    name: 'Table ordering',
  },
  {
    art: RewardArt,
    id: 'square-rewards',
    summary:
      'Members turn their loyalty points into a Square gift card, or into a reward linked to a real Square discount, at a rate you set. The value comes off the bill at your own till, instead of being a promise the guest has to explain to whoever is serving them.',
    line: 'Loyalty points, spendable at the till',
    name: 'Square reward vouchers',
  },
]

/**
 * The set this release displaced.
 *
 * Still built, still released, still worth asking for — just no longer the
 * headline. Kept here rather than deleted so the product's "what's new" page
 * can carry them below the current three, and so the id union above stays
 * honest about every destination a host still has to resolve.
 *
 * When a feature is genuinely old news, delete it from HERE. Nothing should
 * spend more than two releases in this array.
 */
export const PREVIOUS_FEATURES: AnnouncedFeature[] = [
  {
    art: PassArt,
    id: 'wallets',
    summary:
      'A booking confirmation becomes a branded pass in Apple Wallet and Google Wallet, built from your brand kit, that carries the date, time and party size and updates itself when the booking changes. It reminds the guest on their lock screen ahead of the booking, and their loyalty card sits in the wallet alongside it.',
    line: 'Straight into Apple and Google Wallet',
    name: 'Wallet booking confirmations',
  },
  {
    art: CallArt,
    id: 'phone-bookings',
    summary:
      "An AI host answers your venue's line in under a second, at any hour, greets callers by your venue name, checks your live availability and creates the booking straight into the day planner. You choose whether it takes every call or only steps in when nobody picks up, and every call is logged with its outcome and a full transcript.",
    line: 'An AI host answers your line',
    name: 'Phone bookings',
  },
  {
    art: TicketArt,
    id: 'ticketed-events',
    summary:
      "Sell tickets for anything you host, from supper clubs and tastings to live music and New Year's Eve, inside the booking system you already run. Payment is taken at purchase rather than promised at the door, event guests appear in the same day planner as your tables, and every buyer lands in your guest book.",
    line: 'Sell tickets, paid upfront',
    name: 'Ticketed events',
  },
]

export function FeatureAnnouncement({
  actions,
  className,
  contentClassName = 'px-6 py-6 sm:px-8 xl:px-12',
  heading = 'Three new features.',
  surfaceClassName = 'stone-band',
}: {
  /** The host's own CTAs. See the note above on why these are not shared. */
  actions?: ReactNode
  className?: string
  /**
   * The band's inset. Overridable because the gutter is the HOST's, not the
   * block's: on a page whose sections all share one horizontal padding, a band
   * with its own is a misaligned left edge, which is the most visible way a
   * page reads as untidy. Pass the host's section padding here.
   */
  contentClassName?: string
  heading?: string
  /**
   * The band's surface, as a token-scoping class.
   *
   * `stone-band` (default, package stylesheet) is ALWAYS stone in both themes —
   * what the sign-in wants, where the band is the one warm thing on the page.
   *
   * A host whose own canvas is already stone should invert instead, so the band
   * reads as a band rather than as more page: wir passes its `canvas-inverse`
   * (Kitchen on Stone, Stone on Kitchen). Whatever is passed must scope at least
   * `--background`, `--foreground`, `--muted-foreground`, `--border` and the
   * `--primary` pair, since the actions and the figures all resolve against them.
   */
  surfaceClassName?: string
}) {
  return (
    <aside
      aria-label="What's new"
      className={cn(
        surfaceClassName,
        '@container bg-background text-foreground',
        className
      )}
    >
      <div className={cn('flex flex-col gap-6', contentClassName)}>
        <div className="flex flex-col gap-4 @4xl:flex-row @4xl:items-center @4xl:justify-between @4xl:gap-8">
          {/* pill and heading on ONE line: the pill is a marker on the
              announcement, not a label stacked above it */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex shrink-0 items-center gap-2 rounded-full border border-border px-2.5 py-1">
              <span aria-hidden className="size-1.5 rounded-full bg-success" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em]">
                New
              </span>
            </span>
            <h2 className="font-heading text-[clamp(20px,2.2vw,28px)] font-black leading-none tracking-[-0.02em]">
              {heading}
            </h2>
          </div>

          {actions ? (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {actions}
            </div>
          ) : null}
        </div>

        {/* The three, as cells sharing hairlines — no boxes, no cards.

            `items-start`, not `items-center`: a description that wraps makes
            its cell taller, and centring then drops that feature's name off the
            shared baseline the other two sit on. Top-aligned, the three names
            always line up whatever the width does to the copy. */}
        <ul className="grid grid-cols-1 divide-y divide-border border-t border-border @5xl:grid-cols-3 @5xl:divide-x @5xl:divide-y-0">
          {ANNOUNCED_FEATURES.map((feature) => (
            <li
              className="flex items-start gap-4 py-4 @5xl:px-6 @5xl:pb-0 @5xl:pt-6 @5xl:first:pl-0 @5xl:last:pr-0"
              key={feature.name}
            >
              <feature.art />
              <div className="flex min-w-0 flex-col gap-1">
                <span className="text-sm font-semibold leading-tight">
                  {feature.name}
                </span>
                <span className="text-[13px] leading-snug text-muted-foreground">
                  {feature.line}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  )
}
